// Pure calculation helpers ported from RotaSaúde v0.3.4 (identical logic).
export const money = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
export const n = (v) => Number(v) || 0;

export function fmtDuration(mins) {
  mins = Math.max(0, Math.round(mins));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}min`;
}

export function fmtTime(date, withDate = false) {
  if (!date || isNaN(date)) return "—";
  return date.toLocaleString("pt-BR", withDate ? { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" } : { hour: "2-digit", minute: "2-digit" });
}

// Full trip + budget calculation. Returns everything the UI needs.
export function computeBudget(f, teamSel, professionals, manualRate) {
  const rt = !!f.idaVolta;
  const p2p = f.routeMode === "ponto_a_ponto";
  const k1 = n(f.kmIda), k2 = p2p ? 0 : n(f.kmPacienteClinica), k3 = rt ? n(f.kmRetorno) : 0;
  const t1 = n(f.tempoIda), t2 = p2p ? 0 : n(f.tempoPacienteClinica), t3 = rt ? n(f.tempoRetorno) : 0;
  const p1 = n(f.pedagio), p2 = p2p ? 0 : n(f.pedagioPacienteClinica), p3 = rt ? n(f.pedagioRetorno) : 0;
  const km = k1 + k2 + k3, mins = t1 + t2 + t3, ped = p1 + p2 + p3;
  const consumo = Math.max(n(f.consumo), 0.1);
  const litros = km / consumo;
  const comb = litros * n(f.precoComb);

  // schedule
  const s = f.saida ? new Date(f.saida) : new Date();
  const chegadaPaciente = new Date(s.getTime() + t1 * 60000);
  const saidaPaciente = new Date(chegadaPaciente.getTime() + n(f.permanencia) * 60000);
  const chegadaClinica = new Date(saidaPaciente.getTime() + t2 * 60000);
  const saidaClinica = new Date(chegadaClinica.getTime() + (p2p ? 0 : n(f.permanenciaClinica)) * 60000);
  const base = new Date(saidaClinica.getTime() + t3 * 60000);

  // team
  let teamCost = 0;
  const teamNames = [];
  professionals.forEach((pro) => {
    const sel = teamSel[pro.id];
    if (sel && sel.checked) {
      const q = Math.max(1, n(sel.qty) || 1);
      teamCost += q * (Number(pro.value) || 0);
      teamNames.push(`${q}x ${pro.name}`);
    }
  });
  const teamQty = professionals.reduce((sum, pro) => sum + (teamSel[pro.id]?.checked ? Math.max(1, n(teamSel[pro.id].qty) || 1) : 0), 0);

  // costs
  const cComb = comb, cPed = ped;
  const misc = n(f.alimentacao) + n(f.hospedagem) + n(f.estacionamento) + n(f.outras);
  const p = n(f.margem) / 100;
  const costBase = cComb + cPed + misc + teamCost;
  let venda = costBase;
  if (f.modeloMargem === "margem") venda = p >= 1 ? costBase : costBase / (1 - p);
  else venda = costBase * (1 + p);
  venda += n(f.adicional);

  const kmOp = Math.max(km, 0);
  let recomendado = Math.max(venda, n(f.valorMinimo));
  if (manualRate && kmOp > 0 && n(f.custoKm) > 0) {
    recomendado = Math.max(kmOp * n(f.custoKm), n(f.valorMinimo));
  }
  const lucro = recomendado - costBase;
  const margemEf = recomendado ? (lucro / recomendado) * 100 : 0;
  const valorKm = kmOp ? recomendado / kmOp : 0;

  return {
    km, mins, ped, litros, comb, cComb, cPed, misc, teamCost, teamNames, teamQty,
    costBase, venda, recomendado, lucro, margemEf, valorKm, travelCost: comb + ped,
    schedule: { s, chegadaPaciente, saidaPaciente, chegadaClinica, saidaClinica, base, t1, t2, t3, rt },
  };
}

export function buildBudgetText(f, calc, patient, vehicleName, comp) {
  const compName = (comp && comp.name) || "RotaSaúde";
  const docLine = comp && comp.doc ? `\nCNPJ: ${comp.doc}` : "";
  const data = f.dataRemocao ? new Date(f.dataRemocao + "T12:00:00").toLocaleDateString("pt-BR") : "A combinar";
  const patientBlock = patient
    ? `Família/Responsável: ${patient.family || "Não informado"}
Paciente: ${patient.patient || "Não informado"}
Altura: ${patient.height || "Não informado"}
Peso: ${patient.weight || "Não informado"}
Idade: ${patient.age ? `${patient.age} anos` : "Não informado"}
Faz uso de medicamentos: ${patient.usesMedication || "Não informado"}
${patient.usesMedication === "Sim" ? `Medicamentos: ${patient.medications || "Não informado"}\n` : ""}Outras observações clínicas: ${patient.notes || "Nenhuma informada"}

`
    : (f.familia ? `Família/Responsável: ${f.familia}\n\n` : "");
  return `${compName}${docLine}

ORÇAMENTO DE REMOÇÃO

${patientBlock}Data: ${data}
Origem: ${f.orcOrigem || "A definir"}
Local onde o paciente está: ${f.destino || "A definir"}
Unidade de internação / Clínica: ${f.clinicaNome || "A definir"}
Endereço completo da clínica: ${f.clinicaEndereco || "A definir"}
Distância total estimada: ${n(calc.km || f.rKmOperacao).toFixed(1)} km
${vehicleName ? `Veículo: ${vehicleName}\n` : ""}${calc.teamNames.length ? "Equipe prevista: " + calc.teamNames.join(", ") + "\n" : ""}
Valor total da remoção: ${money(calc.recomendado)}

O valor contempla os custos operacionais e de deslocamento considerados neste orçamento. Condições extraordinárias ou alterações de rota podem exigir revisão prévia do valor.

— Tecnologia RotaSaúde`;
}

export function normalizeWhatsPhone(value) {
  let digits = (value || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length === 10 || digits.length === 11) digits = "55" + digits;
  return digits;
}
